import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';

import { AccountDetails } from '@pages/AccountTransactions/AccountDetails';
import {
  useGetVirtualBankAccountsAccountNumberMovements,
  type Movement
} from 'api';
import { Restricted } from 'containers/Restricted';

import { useOrganisationId } from 'hooks/context/useOrganisationId';
import { useGlobalFilters } from 'hooks/useGlobalFilters';
import { useHandleRowClick } from 'hooks/useHandleRowClick';
import { useVirtualList } from 'hooks/useVirtualList';

import { ErrorPanel } from '@atoms/ErrorPanel';
import { Spinner } from '@atoms/Spinner/Spinner';

import { DataTable } from 'components/ui/organisms/DataTable';
import { TableLayout } from 'components/ui/templates/TableLayout/TableLayout';

import { AccountTransactionsHeader } from './AccountTransactionsHeader';
import { apiParams } from './apiAdapter';
import {
  ACCOUNT_TRANSACTION_TABLE_TEST_ID,
  CREATE_STATEMENT_BUTTON_TEST_ID,
  ERROR_MESSAGE,
  ERROR_MESSAGE_TEST_ID
} from './constants';
import { CreateStatementButton } from './CreateStatementButton';
import { useLocalState } from './localState';
import { useTransactionsColumns } from './useTransactionsColumns';

export const AccountTransactionsTable = () => {
  const { virtual_account_number: accountNumber } = useParams();

  const accountNumberRef = useRef<string | undefined>(accountNumber);

  // HOOKS
  const organisationId = useOrganisationId<number>();
  const columnDefinitions = useTransactionsColumns();

  const { filters: globalFilters } = useGlobalFilters();

  const {
    state: localState,
    updateFilters: setFilters,
    resetState,
    updateCursorAfter
  } = useLocalState(undefined, accountNumber);

  useEffect(() => {
    accountNumberRef.current = accountNumber;
  }, [accountNumber]);

  // CALLBACKS
  const {
    data: movements,
    isFetching,
    isError
  } = useGetVirtualBankAccountsAccountNumberMovements(
    organisationId,
    accountNumberRef.current as unknown as number,
    apiParams({ ...localState, ...globalFilters }),
    {
      query: {
        enabled: Boolean(
          globalFilters.dateRange.from &&
            globalFilters.dateRange.to &&
            accountNumberRef.current &&
            organisationId
        )
      }
    }
  );

  const { onScroll, data } = useVirtualList<Movement>({
    data: movements?.data,
    initialRefValue: movements?.meta?.cursor_next,
    uniqueKey: 'id',
    isFetching,
    updateCursorCallback: updateCursorAfter,
    resetConditions: [
      globalFilters,
      localState.filters,
      organisationId,
      accountNumber,
      updateCursorAfter
    ],
    cursorNext: movements?.meta?.has_next_page
      ? movements?.meta?.cursor_next
      : undefined
  });

  const [selectedRowData, setSelectedRowData] = useState<Movement>();
  const handleRowClick = useHandleRowClick<Movement>(setSelectedRowData);

  if (!accountNumberRef.current || !accountNumber) {
    return <Spinner />;
  }

  return (
    <TableLayout
      header={
        <AccountTransactionsHeader
          accountNumber={accountNumber}
          filters={localState.filters}
          setFilters={setFilters}
          resetState={resetState}
        />
      }
      content={
        isError ? (
          <div className="flex w-full items-center justify-center">
            <ErrorPanel
              data-testid={ERROR_MESSAGE_TEST_ID}
              message={ERROR_MESSAGE}
              variant="error"
            />
          </div>
        ) : (
          <DataTable
            key={accountNumberRef.current}
            total={movements?.meta?.total}
            tableActions={
              <Restricted requiredPermission="exports:manage">
                <CreateStatementButton
                  data-testid={CREATE_STATEMENT_BUTTON_TEST_ID}
                  accountNumber={accountNumber}
                />
              </Restricted>
            }
            dataSource={data}
            columns={columnDefinitions}
            onScroll={onScroll}
            onRowClick={handleRowClick}
            isLoading={isFetching}
            rowKey="id"
            selectedRow={selectedRowData}
            tableDataTestId={ACCOUNT_TRANSACTION_TABLE_TEST_ID}
            rowDataTestId="account-transactions-table-row-entry"
            rightPanel={
              <AccountDetails
                accountNumber={accountNumber}
                fromDate={globalFilters.dateRange.from}
                toDate={globalFilters.dateRange.to}
              />
            }
          />
        )
      }
    />
  );
};
